import { t } from 'lib/i18n'
import { enqueueMutation, processQueue } from 'lib/offlineQueue'

export const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '')

const TOKEN_KEY = 'jopath.token'

export class ApiError extends Error {
  status?: number
  code?: string
  details?: string[]

  constructor(message: string, status?: number, code?: string, details?: string[]) {
    super(message)
    this.status = status
    this.code = code
    this.details = details
  }
}

export const tokenStore = {
  clear: () => {
    localStorage.removeItem(TOKEN_KEY)
    sessionStorage.removeItem(TOKEN_KEY)
  },
  get: () => localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY),
  /** `remember` keeps the token in localStorage (survives browser restarts); otherwise it's sessionStorage-only. */
  set: (token: string, remember = true) => {
    tokenStore.clear()
    ;(remember ? localStorage : sessionStorage).setItem(TOKEN_KEY, token)
  },
}

let unauthorizedHandler: () => void = () => undefined

export const onUnauthorized = (handler: () => void) => {
  unauthorizedHandler = handler
}

const authHeaders = (): Record<string, string> => {
  const token = tokenStore.get()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

const networkError = () => new ApiError(t('تعذر الاتصال بالخادم، تحقق من الشبكة وحاول مجدداً', 'Could not reach the server. Check the network and try again.'))

const isMutation = (query: string): boolean => /^\s*mutation\b/.test(query)

export const gql = async <T>(query: string, variables?: Record<string, unknown>): Promise<T> => {
  let response: Response
  try {
    response = await fetch(`${API_URL}/graphql`, {
      body: JSON.stringify({ query, variables }),
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      method: 'POST',
    })
  } catch {
    // Genuinely offline (fetch itself failed, not a server error): queue mutations for automatic
    // retry once back online, instead of silently losing the attempt (reads can't be queued — there's
    // nothing honest to return for a failed query).
    if (isMutation(query)) enqueueMutation(query, variables)
    throw networkError()
  }
  const body = await response.json().catch(() => null)
  const error = body?.errors?.[0]
  if (error) {
    const code = error.extensions?.code
    if (code === 'UNAUTHENTICATED') unauthorizedHandler()
    throw new ApiError(error.message, response.status, code, error.extensions?.details)
  }
  if (!response.ok || !body?.data) throw networkError()
  return body.data as T
}

/** Replays any mutations queued while offline. Safe to call repeatedly (e.g. on every `online` event
 *  and from a manual "retry now" button) — processQueue is a no-op while already running. */
export const retryQueuedMutations = (onItemSynced?: (label: string) => void): Promise<void> => processQueue(gql, onItemSynced)

const restError = async (response: Response) => {
  const body = await response.json().catch(() => null)
  if (response.status === 401) unauthorizedHandler()
  return new ApiError(body?.message || t('تعذر تنفيذ الطلب', 'The request could not be completed'), response.status, body?.code, body?.details)
}

const fileNameFrom = (response: Response, fallback: string) => {
  const header = response.headers.get('Content-Disposition') || ''
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (encoded) return decodeURIComponent(encoded[1])
  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain ? plain[1] : fallback
}

export const fetchBlob = async (path: string): Promise<{ blob: Blob; fileName: string }> => {
  let response: Response
  try {
    response = await fetch(`${API_URL}/api${path}`, { headers: authHeaders() })
  } catch {
    throw networkError()
  }
  if (!response.ok) throw await restError(response)
  return { blob: await response.blob(), fileName: fileNameFrom(response, 'download') }
}

export const download = async (path: string) => {
  const { blob, fileName } = await fetchBlob(path)
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export const upload = async <T>(path: string, form: FormData, method: 'POST' | 'PUT' = 'POST'): Promise<T> => {
  let response: Response
  try {
    response = await fetch(`${API_URL}/api${path}`, { body: form, headers: authHeaders(), method })
  } catch {
    throw networkError()
  }
  if (!response.ok) throw await restError(response)
  return response.json()
}

export const removeRest = async <T>(path: string): Promise<T> => {
  let response: Response
  try {
    response = await fetch(`${API_URL}/api${path}`, { headers: authHeaders(), method: 'DELETE' })
  } catch {
    throw networkError()
  }
  if (!response.ok) throw await restError(response)
  return response.json()
}
